import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Download, Loader2, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import SchoolHeader from "../school/components/SchoolHeader";
import AdminSideBar from "./AdminSideBar";
import DateRangeFilter from "./components/DateRangeFilter";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const PAGE_SIZES = [5, 10, 25, 50, 100];
const EMPTY_PAGINATION = { page: 1, pageSize: 10, total: 0, totalPages: 1 };

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("en-IN");
};

const examValue = (value) => (
  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${value ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
    {value ? "Yes" : "No"}
  </span>
);

const RegisteredSchools = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("uc_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const token = localStorage.getItem("uc_admin_token");
    if (!token) {
      handleUnauthorized();
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);
    axios.get(`${API_BASE_URL}/school-registration/registered-schools`, {
      params: { page, pageSize, search, dateFrom, dateTo },
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    }).then((response) => {
      setSchools(Array.isArray(response.data?.data) ? response.data.data : []);
      setPagination(response.data?.pagination || { ...EMPTY_PAGINATION, page, pageSize });
    }).catch((error) => {
      if (error.code === "ERR_CANCELED") return;
      if (error.response?.status === 401) return handleUnauthorized();
      toast.error(error.response?.data?.message || "Unable to load registered schools");
    }).finally(() => {
      if (!controller.signal.aborted) setIsLoading(false);
    });

    return () => controller.abort();
  }, [handleUnauthorized, page, pageSize, search, dateFrom, dateTo]);

  const exportExcel = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/school-registration/registered-schools/export`, {
        params: { search, dateFrom, dateTo },
        headers: { Authorization: `Bearer ${localStorage.getItem("uc_admin_token")}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "registered-schools.xls";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch (error) {
      if (error.response?.status === 401) return handleUnauthorized();
      toast.error("Unable to export registered schools");
    } finally {
      setIsExporting(false);
    }
  };

  const firstEntry = pagination.total ? ((pagination.page - 1) * pagination.pageSize) + 1 : 0;
  const lastEntry = Math.min(pagination.page * pagination.pageSize, pagination.total);

  return (
    <div className="min-h-screen bg-gradient-hero">
      <SchoolHeader />
      <div className="flex flex-col bg-slate-50 lg:flex-row">
        <AdminSideBar />
        <main className="min-w-0 flex-1 px-3 py-5 sm:px-5">
          <section className="mx-auto max-w-[1600px] space-y-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="space-y-2 text-left">
                <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Registered Schools</h1>
              </div>
              <Button onClick={exportExcel} disabled={isExporting || isLoading}>
                {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                {isExporting ? "Exporting..." : "Export Excel"}
              </Button>
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 xl:flex-row xl:items-end xl:justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>Show</span>
                <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
                  <SelectTrigger className="w-24 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAGE_SIZES.map((size) => <SelectItem key={size} value={String(size)}>{size}</SelectItem>)}</SelectContent>
                </Select>
                <span>entries</span>
              </div>
              <DateRangeFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                onChange={(range) => { setDateFrom(range.dateFrom); setDateTo(range.dateTo); setPage(1); }}
              />
              <div className="relative w-full xl:max-w-md">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  className="bg-white pl-9"
                  placeholder="Search school, place, mode, or exam"
                  aria-label="Search by registration number, school name, place, exam mode, or selected exam"
                />
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200">
              <Table>
                <TableHeader className="bg-slate-100">
                  <TableRow>
                    {["ID", "School name", "Students", "Exam mode", "NSTSE", "UIEO", "UIMO", "UCO", "UIGKO", "Registration date", "Country"].map((heading) => (
                      <TableHead key={heading} className="whitespace-nowrap font-bold text-slate-700">{heading}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow><TableCell colSpan={11} className="h-32 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-sky-600" /><span className="mt-2 block text-slate-500">Loading schools...</span></TableCell></TableRow>
                  ) : schools.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="h-32 text-center text-slate-500">No registered schools found.</TableCell></TableRow>
                  ) : schools.map((school) => (
                    <TableRow
                      key={school.SchoolId}
                      className="cursor-pointer focus:bg-sky-50 focus:outline-none hover:bg-sky-50"
                      tabIndex={0}
                      role="link"
                      aria-label={`View details for ${school.SchoolName}`}
                      onClick={() => navigate(`/admin/registered-schools/${school.SchoolId}`)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") navigate(`/admin/registered-schools/${school.SchoolId}`);
                      }}
                    >
                      <TableCell className="font-semibold text-sky-700">{school.SchoolId}</TableCell>
                      <TableCell className="font-medium text-slate-900">
                        <div className="w-64 truncate" title={school.SchoolName || ""}>
                          {school.SchoolName || "—"}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-slate-700">{school.studentCount ?? 0}</TableCell>
                      {/* <TableCell>{school.Place || "—"}</TableCell>
                      <TableCell>{school.Schoolprinciname || "—"}</TableCell>
                      <TableCell>{school.PrinciContact || "—"}</TableCell>
                      <TableCell>{school.Password || "—"}</TableCell> */}
                      <TableCell>{school.Exam_Mode || "---"}</TableCell>
                      <TableCell>{examValue(school.IsNSTSEselected)}</TableCell>
                      <TableCell>{examValue(school.IsUIEOSelected)}</TableCell>
                      <TableCell>{examValue(school.IsUIMOSelected)}</TableCell>
                      <TableCell>{examValue(school.IsUCOSelected)}</TableCell>
                      <TableCell>{examValue(school.IsUIGKOSelected)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(school.Updated_Date)}</TableCell>
                      <TableCell>{school.Country || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
              <span>Showing {firstEntry} to {lastEntry} of {pagination.total} entries</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={isLoading || page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft className="mr-1 h-4 w-4" />Previous</Button>
                <span className="rounded-md bg-sky-50 px-3 py-2 font-semibold text-sky-700">Page {pagination.page} of {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={isLoading || page >= pagination.totalPages} onClick={() => setPage((value) => value + 1)}>Next<ChevronRight className="ml-1 h-4 w-4" /></Button>
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default RegisteredSchools;
